import { useEffect, useRef, useState, useCallback, type MouseEvent } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useKernel } from "@/kernel/useKernel";
import CodeWorkspace from "@/components/CodeWorkspace";
import ThemeToggle from "@/theme/ThemeToggle";
import AppLauncher from "@/components/AppLauncher";
import UserMenu from "@/components/UserMenu";
import InstructionPanel from "@/components/InstructionPanel";
import StepNav from "@/components/StepNav";
import Button from "@/components/Button";
import Skeleton from "@/components/Skeleton";
import PanelLeftIcon from "@/components/PanelLeftIcon";
import Icon from "@/components/Icon";
import Logo from "@/components/Logo";
import { useToast } from "@/components/Toast";
import { getExercise, completeStep, saveSnapshot, loadSnapshot, getStepTest, getStepScaffold } from "@/api/exercises";
import { getStepPack, type RuntimePack } from "@/api/packs";
import type { Scaffold } from "@/kernel/KernelContext";
import { slugify } from "@/utils/download";
import type { ExerciseDetail } from "@/types";

const AUTOSAVE_INTERVAL_MS = 30_000;
const LEFT_MIN = 0;   // px — panel gauche peut être complètement caché
const LEFT_MAX = 700; // px
const LEFT_DEFAULT = 320;

export default function Exercise() {
  const { id }          = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate        = useNavigate();
  const location        = useLocation();
  const kernel          = useKernel();
  const toast           = useToast();

  // Cours d'origine (passé via l'état de route depuis la liste) → retour ciblé vers
  // le contenu du cours. Une série pouvant appartenir à plusieurs cours, on ne peut
  // pas le déduire seul.
  const origin = (location.state as { courseId?: number; chapterKey?: string } | null) ?? null;
  const backToList = () =>
    navigate(
      origin?.courseId ? `/exercises/c/${origin.courseId}` : "/exercises",
      { state: origin?.chapterKey ? { chapterKey: origin.chapterKey } : undefined },
    );

  // Largeur du panneau gauche (instructions), draggable
  const [leftWidth, setLeftWidth]   = useState(LEFT_DEFAULT);
  const dragging                    = useRef(false);
  const dragStartX                  = useRef(0);
  const dragStartWidth              = useRef(LEFT_DEFAULT);

  const onDragStart = useCallback((e: MouseEvent) => {
    dragging.current     = true;
    dragStartX.current   = e.clientX;
    dragStartWidth.current = leftWidth;
    document.body.style.cursor    = "col-resize";
    document.body.style.userSelect = "none";
  }, [leftWidth]);

  useEffect(() => {
    const onMove = (e: globalThis.MouseEvent) => {
      if (!dragging.current) return;
      const delta = e.clientX - dragStartX.current;
      const next  = Math.max(LEFT_MIN, Math.min(LEFT_MAX, dragStartWidth.current + delta));
      setLeftWidth(next);
    };
    const onUp = () => {
      dragging.current = false;
      document.body.style.cursor    = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const [exercise, setExercise]         = useState<ExerciseDetail | null>(null);
  const [activeStep, setActiveStep]     = useState(0);
  const [code, setCode]                 = useState("");
  // Scaffold prof (caché à l'élève) chargé une fois par étape ; composé autour du
  // code élève à chaque clic Exécuter/Valider via kernel.run / kernel.runTest.
  const [scaffold, setScaffold]         = useState<{ scaffold_pre: string; scaffold_post: string } | null>(null);
  // Pack de jeu du step (cf. plan gamelib P1.5). Fetché en parallèle du scaffold.
  const [pack, setPack]                 = useState<RuntimePack | null>(null);
  const [feedback, setFeedback]         = useState<string | null>(null);
  // Vrai uniquement quand le dernier retour est un succès (étape validée).
  const [feedbackOk, setFeedbackOk]     = useState(false);
  const [saveStatus, setSaveStatus]     = useState<"idle" | "saving" | "saved">("idle");
  const [navOpen, setNavOpen]           = useState(true);
  // Validation en cours (le bouton « Valider » reste toujours disponible pour relancer).
  const [checking, setChecking]         = useState(false);

  // Garde une ref pour l'autosave (évite les stale closures)
  const codeRef    = useRef(code);
  const stepIdRef  = useRef<number | null>(null);
  const savedTimer = useRef<number | undefined>(undefined);
  codeRef.current  = code;

  // Sauvegarde le code d'une étape en montrant l'état (Enregistrement… → Enregistré ✓).
  const persist = useCallback((stepId: number | null, text: string) => {
    if (stepId == null) return;
    setSaveStatus("saving");
    saveSnapshot(stepId, text)
      .then(() => {
        setSaveStatus("saved");
        window.clearTimeout(savedTimer.current);
        savedTimer.current = window.setTimeout(() => setSaveStatus("idle"), 2000);
      })
      .catch(() => setSaveStatus("idle"));
  }, []);

  // Redirige si non connecté
  useEffect(() => {
    if (!authLoading && !user?.authenticated) navigate("/login");
  }, [authLoading, user, navigate]);

  // Charge l'exercice
  useEffect(() => {
    if (!id) return;
    getExercise(Number(id)).then((ex) => {
      setExercise(ex);
      setActiveStep(0);
    });
  }, [id]);

  // Vide la console quand on change d'exercice
  useEffect(() => {
    kernel.clearOutput();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charge le snapshot UNIQUEMENT au vrai changement d'étape (ou au chargement
  // de l'exercice). Dépendre de `exercise` entier rechargeait le snapshot — et
  // écrasait le code de l'élève — à chaque mutation (ex. après validation).
  useEffect(() => {
    if (!exercise) return;
    const step = exercise.steps[activeStep];
    if (!step) return;

    // Flush du code de l'étape précédente avant de basculer (sinon perte si < 30 s).
    const prevStepId = stepIdRef.current;
    if (prevStepId !== null && prevStepId !== step.id) {
      persist(prevStepId, codeRef.current);
    }
    stepIdRef.current = step.id;

    loadSnapshot(step.id).then(({ code: saved }) => {
      setCode(saved ?? step.starter_code ?? "");
    });
    // Récupère le scaffold caché (pre/post) — composé autour du code élève à chaque
    // Exécuter/Valider. Garde-fou : si erreur (étape sans scaffold), on continue
    // avec un scaffold vide (= comportement actuel sans composition).
    getStepScaffold(step.id)
      .then(setScaffold)
      .catch(() => setScaffold({ scaffold_pre: "", scaffold_post: "" }));
    getStepPack(step.id)
      .then(({ pack }) => setPack(pack))
      .catch(() => setPack(null));
    setFeedback(null);
  }, [exercise?.id, activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave toutes les 30s
  useEffect(() => {
    const interval = setInterval(() => {
      persist(stepIdRef.current, codeRef.current);
    }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [persist]);

  // Flush à la fermeture de l'onglet et au démontage (navigation interne).
  useEffect(() => {
    const flush = () => {
      if (stepIdRef.current !== null) {
        saveSnapshot(stepIdRef.current, codeRef.current).catch(() => {});
      }
    };
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      flush();
    };
  }, []);

  // Cmd/Ctrl+S : enregistre le code sur le site (et bloque la sauvegarde HTML du navigateur).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        persist(stepIdRef.current, codeRef.current);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [persist]);

  // Change d'étape : réinitialise la console (contexte neuf) ; le fondu est géré au rendu.
  const changeStep = useCallback((index: number) => {
    setActiveStep(index);
    kernel.clearOutput();
    setFeedback(null);
  }, [kernel]);

  const handleValidate = useCallback(async () => {
    if (!exercise || !kernel.ready) return;
    const step = exercise.steps[activeStep];
    if (!step) return;

    setChecking(true);
    setFeedback(null);
    kernel.clearOutput();

    try {
      let passed = true;

      if (step.validation_type === "auto_test" || step.validation_type === "both") {
        // Le test_code n'est pas exposé dans le détail public → appel authentifié dédié.
        const { test_code } = await getStepTest(step.id);
        // Scaffold composé autour du code élève (cf. plan gamelib P0/P1.5). Si
        // l'étape n'a ni scaffold ni pack, runTest se comporte comme avant.
        const sc: Scaffold | undefined = scaffold
          ? { pre: scaffold.scaffold_pre, post: scaffold.scaffold_post, pack: pack ?? undefined }
          : undefined;
        passed = await kernel.runTest(code, test_code, sc);
      }

      if (passed) {
        const result = await completeStep(step.id);
        // Marque l'étape validée localement (→ apparition de « Étape suivante »).
        setExercise((prev) => {
          if (!prev) return prev;
          const steps = prev.steps.map((s, i) =>
            i === activeStep
              ? { ...s, progress: { completed_at: new Date().toISOString(), hints_used: result.hints_used, score: result.score } }
              : s,
          );
          return { ...prev, steps };
        });
        // Pas d'avance automatique : l'élève reste maître de son rythme et peut
        // relancer le test (« Revalider ») ou avancer (« Exercice suivant »).
        // Succès → toast (le feedback inline est réservé aux erreurs, pour ne pas
        // chevaucher les boutons de la barre d'actions).
        setFeedback(null);
        setFeedbackOk(true);
        toast.success("Exercice validé");
      } else {
        setFeedback("Test non réussi. Relis la consigne et réessaie.");
        setFeedbackOk(false);
      }
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : "Erreur lors de la validation.");
      setFeedbackOk(false);
    } finally {
      setChecking(false);
    }
  }, [exercise, activeStep, code, kernel, toast]);

  const goNext = useCallback(() => {
    if (!exercise) return;
    if (activeStep < exercise.steps.length - 1) changeStep(activeStep + 1);
  }, [exercise, activeStep, changeStep]);

  if (!exercise) {
    return (
      <div style={styles.layout}>
        <div style={styles.topbar}>
          <Skeleton width={80} height={26} radius="var(--radius-sm)" />
          <Skeleton width={220} height={18} style={{ flex: "none" }} />
        </div>
        <div style={styles.body}>
          <div style={{ ...styles.left, width: LEFT_DEFAULT, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            <Skeleton height={20} width="60%" />
            <Skeleton height={120} />
            <Skeleton height={14} />
            <Skeleton height={14} width="80%" />
          </div>
          <div style={styles.splitter} />
          <div style={{ ...styles.right, padding: 16 }}>
            <Skeleton height="100%" />
          </div>
        </div>
      </div>
    );
  }

  const currentStep = exercise.steps[activeStep];
  const multiStep   = exercise.steps.length > 1;
  const downloadFilename = `${slugify(exercise.title) || "script"}_exercice${(currentStep?.order ?? 0) + 1}.py`;

  // Boutons propres à l'exercice, injectés dans la barre d'actions de CodeWorkspace.
  const exerciseActions = (
    <>
      {feedback && (
        <span style={{ ...styles.feedback, color: feedbackOk ? "var(--green)" : "var(--red)" }}>
          {feedbackOk && <Icon name="done" size={14} />}
          {feedback}
        </span>
      )}
      {currentStep && currentStep.validation_type !== "manual" && (
        <>
          <Button variant="primary" onClick={handleValidate} disabled={checking} style={styles.validateBtn}>
            {checking ? (
              <><span className="spin" /> Vérification…</>
            ) : currentStep.progress ? (
              "Revalider"
            ) : (
              "Valider l'exercice"
            )}
          </Button>
          {currentStep.progress && activeStep < exercise.steps.length - 1 && (
            <Button variant="success" onClick={goNext} style={styles.nextBtn}>
              Exercice suivant
              <Icon name="next" size={14} />
            </Button>
          )}
        </>
      )}
    </>
  );

  return (
    <div style={styles.layout}>
      {/* Barre du haut. La bascule reste TOUJOURS sur cette ligne (jamais dans
          l'en-tête du tiroir) : elle se contente de glisser horizontalement. */}
      <div style={styles.topbar}>
        <div style={{ ...styles.leftZone, ...(navOpen ? styles.leftZoneOpen : {}) }}>
          <button
            style={styles.iconToggle}
            onClick={() => navigate("/")}
            aria-label="Retour à l'accueil"
            title="Retour à l'accueil"
          >
            <Logo size={24} />
          </button>
          <button
            style={styles.iconToggle}
            onClick={() => setNavOpen((o) => !o)}
            aria-label={navOpen ? "Masquer le panneau" : "Afficher le panneau"}
            aria-expanded={navOpen}
            title={navOpen ? "Masquer le panneau" : "Afficher le panneau"}
          >
            <PanelLeftIcon />
          </button>
        </div>
        <span style={styles.exerciseTitle}>{exercise.title}</span>
        {multiStep && (
          <span style={styles.stepIndicator}>
            Exercice {activeStep + 1} / {exercise.steps.length}
          </span>
        )}
        {saveStatus !== "idle" && (
          <span style={styles.saveStatus}>
            {saveStatus === "saving" ? (
              "Enregistrement…"
            ) : (
              <>
                Enregistré
                <Icon name="done" size={13} style={{ color: "var(--green)" }} />
              </>
            )}
          </span>
        )}
        <div style={styles.kernelStatus}>
          {kernel.error ? (
            <span style={styles.statusItem} title={kernel.error}>
              <Icon name="status" size={9} style={{ color: "var(--red)" }} />
              <span style={{ color: "var(--red)" }}>Erreur kernel</span>
            </span>
          ) : kernel.ready ? (
            <span style={styles.statusItem}>
              <Icon name="status" size={9} style={{ color: "var(--green)" }} />
              <span style={{ color: "var(--green)" }}>Prêt</span>
            </span>
          ) : (
            <span style={styles.statusItem}>
              <Icon name="status" size={9} style={{ color: "var(--yellow)" }} />
              <span style={{ color: "var(--yellow)" }}>
                Chargement Pyodide… (peut prendre 30–60s)
              </span>
            </span>
          )}
        </div>
        <ThemeToggle />
        <AppLauncher />
        <UserMenu />
      </div>

      {/* Corps principal */}
      <div style={styles.body}>
        {/* Tiroir latéral — comportement « push » (pousse le contenu vers la droite). */}
        {navOpen && (
          <StepNav
            steps={exercise.steps}
            activeIndex={activeStep}
            onSelect={changeStep}
            onBack={backToList}
          />
        )}

        {/* Panneau gauche : consigne (largeur ajustable) */}
        <div style={{ ...styles.left, width: leftWidth, display: leftWidth === 0 ? "none" : undefined }}>
          {currentStep && (
            <div key={activeStep} className="fade-in" style={{ height: "100%" }}>
              <InstructionPanel step={currentStep} />
            </div>
          )}
        </div>

        {/* Poignée de redimensionnement */}
        <div
          className="resize-handle"
          style={styles.splitter}
          onMouseDown={onDragStart}
          title="Glisser pour redimensionner / double-clic pour masquer"
          onDoubleClick={() => setLeftWidth(leftWidth === 0 ? LEFT_DEFAULT : 0)}
        />

        {/* Panneau droit : atelier de code mutualisé (éditeur + console + barre) */}
        <CodeWorkspace
          code={code}
          onChange={setCode}
          downloadFilename={downloadFilename}
          extraActions={exerciseActions}
          scaffold={scaffold
            ? { pre: scaffold.scaffold_pre, post: scaffold.scaffold_post, pack: pack ?? undefined }
            : undefined}
        />
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  layout: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    background: "var(--bg-base)",
    overflow: "hidden",
  },
  topbar: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "8px 16px",
    background: "var(--bg-bar)",
    borderBottom: "1px solid var(--border-soft)",
    flexShrink: 0,
  },
  leftZone: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
  },
  leftZoneOpen: {
    width: 224, // 240 (tiroir) − 16 (padding gauche de la barre)
    justifyContent: "space-between",
  },
  iconToggle: {
    background: "transparent",
    border: "none",
    color: "var(--text)",
    padding: 6,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "var(--radius-sm)",
    cursor: "pointer",
  },
  exerciseTitle: { fontWeight: 600, color: "var(--text)", flex: 1 },
  stepIndicator: { fontSize: 12, color: "var(--text-muted)", fontWeight: 600 },
  saveStatus: {
    fontSize: 12,
    color: "var(--text-muted)",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  kernelStatus: { fontSize: 12 },
  statusItem: { display: "inline-flex", alignItems: "center", gap: 6 },
  body: { display: "flex", flex: 1, overflow: "hidden" },
  left: { flexShrink: 0, overflow: "hidden" },
  splitter: {
    width: 10,
    flexShrink: 0,
    background: "var(--bg-panel)",
    cursor: "col-resize",
    transition: "background 0.15s",
    backgroundClip: "content-box",
    paddingLeft: 4,
    paddingRight: 3,
  },
  right: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  feedback: {
    fontSize: 13,
    fontWeight: 600,
    marginLeft: 8,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    minWidth: 0,
    flexShrink: 1,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  validateBtn: { marginLeft: "auto", padding: "8px 22px" },
  nextBtn: { padding: "8px 22px" },
};
